import React, { useState, useEffect } from 'react'
import imageCompression from 'browser-image-compression'
import LoadingSpinner from '../common/LoadingSpinner'
import { generateProductCode } from '../../utils/formatters'
import './ProductModal.scss'

const ProductModal = ({
  isOpen,
  onClose,
  product = null,
  onSave,
  loading,
  nextProductId,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    size: '',
    color: '',
    basePrice: '',
    initialStock: '',
    images: [],
  })
  const [imageLoading, setImageLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    if (product) {
      setFormData({
        name: product.name,
        size: product.size || '',
        color: product.color || '',
        basePrice: product.basePrice,
        initialStock: product.initialStock || '',
        images: product.images || [],
      })
    } else {
      setFormData({
        name: '',
        size: '',
        color: '',
        basePrice: '',
        initialStock: '',
        images: [],
      })
    }
  }, [product, isOpen])

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files)
    if (!files || files.length === 0) return

    // Vérifie la taille brute avant toute compression
    const tooLarge = files.find((file) => file.size > 5 * 1024 * 1024)
    if (tooLarge) {
      alert(`L'image "${tooLarge.name}" est trop lourde (max 5 Mo).`)
      return
    }

    setImageLoading(true) // Active le chargement

    // Traite toutes les images en parallèle pour gagner du temps
    const imagePromises = files.map(async (file) => {
      try {
        const options = {
          maxSizeMB: 1.5, // un peu plus souple
          maxWidthOrHeight: 1920, // réduit la résolution si > 1920px
          useWebWorker: true,
          initialQuality: 0.85, // légère compression, bonne qualité
          fileType: 'image/jpeg', // JPEG est plus rapide que WebP sur mobile
        }

        // Compression rapide
        const optimizedFile = await imageCompression(file, options)

        // Conversion en base64
        const base64 = await imageCompression.getDataUrlFromFile(optimizedFile)

        return base64
      } catch (error) {
        // console.error("Erreur lors de l'optimisation de l'image :", error)
        return null
      }
    })

    // Attend toutes les compressions en parallèle
    const compressedImages = await Promise.all(imagePromises)

    // Filtre les erreurs et met à jour le state une seule fois
    const validImages = compressedImages.filter(Boolean)
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...validImages],
    }))

    setImageLoading(false) // Désactive le chargement

    // Réinitialise l'input pour permettre de reuploader les mêmes fichiers
    e.target.value = ''
  }

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const productData = {
      ...formData,
      basePrice: parseFloat(formData.basePrice),
      initialStock: parseInt(formData.initialStock) || 0,
    }

    if (!product) {
      productData.id = Date.now()
      productData.code = generateProductCode(nextProductId)
      productData.createdAt = new Date().toISOString()
    } else {
      productData.code = product.code
      productData.createdAt = product.createdAt
    }

    onSave(productData, product)
  }

  if (!isOpen) return null

  return (
    <div className='modal-overlay'>
      <div className='modal-content sm'>
        <div className='modal-header'>
          <h2 className='modal-title'>
            {product ? 'Modifier le produit' : 'Ajouter un produit'}
          </h2>

          <form onSubmit={handleSubmit} className='form-group'>
            <div className='form-field'>
              <label>Nom du produit *</label>
              <input
                type='text'
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </div>

            <div className='form-field'>
              <label>Taille (S, M, L, XL...)</label>
              <input
                type='text'
                value={formData.size}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, size: e.target.value }))
                }
                placeholder='ex: S, M, L, XL, 36, 38, 40...'
              />
            </div>

            <div className='form-field'>
              <label>Couleur</label>
              <input
                type='text'
                value={formData.color}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, color: e.target.value }))
                }
                placeholder='ex: Rouge, Bleu, Noir, Blanc...'
              />
            </div>

            <div className='form-field'>
              <label>Prix (GNF) *</label>
              <input
                type='number'
                value={formData.basePrice}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    basePrice: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div className='form-field'>
              <label>Stock initial</label>
              <input
                type='number'
                value={formData.initialStock}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    initialStock: e.target.value,
                  }))
                }
              />
            </div>

            <div className='form-field'>
              <label>Images (plusieurs possibles)</label>
              <input
                type='file'
                accept='image/*'
                multiple
                onChange={handleImageUpload}
                disabled={imageLoading}
              />

              {imageLoading && (
                <div className='mt-3 text-center'>
                  <LoadingSpinner size='sm' />
                  <p className='text-sm mt-2'>Optimisation des images...</p>
                </div>
              )}

              {formData.images.length > 0 && (
                <div className='mt-3'>
                  <p className='text-sm mb-2'>
                    {formData.images.length} image(s) ajoutée(s)
                  </p>
                  <div className='image-grid'>
                    {formData.images.map((image, index) => (
                      <div key={index} className='image-item'>
                        <img src={image} alt={`Imag ${index + 1}`} />
                        <button
                          type='button'
                          onClick={() => removeImage(index)}
                          className='remove-btn'
                          disabled={imageLoading}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className='form-row pt-4' style={{ flexDirection: 'row' }}>
              <button
                type='button'
                onClick={onClose}
                className='btn outline flex-1'
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type='submit'
                className='btn primary flex-1'
                disabled={loading}
              >
                {loading ? (
                  <>
                    <LoadingSpinner size='sm' />
                    {product ? 'Modification...' : 'Ajout...'}
                  </>
                ) : product ? (
                  'Modifier'
                ) : (
                  'Ajouter'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ProductModal
